import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Collapse,
} from '@mui/material';
import { useNotify, usePermissions } from 'react-admin';
import {
    getOvertimeRequests,
    deleteOvertimeRequest,
    OvertimeRequest,
} from '../dataProvider';

/**
 * OvertimeList component displaying the employee's overtime declarations.
 *
 * Each row shows date, duration, task description, status, and inline history.
 * Delete/edit actions are restricted to declarations whose date falls within
 * the current calendar month.
 */
export const OvertimeList: React.FC = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const { permissions } = usePermissions<string[]>();

    /** `true` when the current user holds the `overtime.view_all` permission. */
    const canViewAll = Array.isArray(permissions) && permissions.includes('overtime.view_all');

    const [requests, setRequests] = useState<OvertimeRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<number | null>(null);
    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    /**
     * Fetches overtime declarations from the API.
     * Wrapped in useCallback to satisfy the exhaustive-deps lint rule.
     */
    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getOvertimeRequests();
            setRequests(data);
        } catch (error) {
            console.error('Error fetching overtime requests:', error);
            notify('Failed to load overtime declarations', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    /**
     * Checks if a date falls within the current calendar month.
     *
     * @param dateStr - ISO 8601 date string (YYYY-MM-DD).
     * @returns `true` if the date is in the current month.
     */
    const isCurrentMonth = (dateStr: string): boolean => {
        const now = new Date();
        const d = new Date(dateStr + 'T00:00:00Z');
        return d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth();
    };

    /**
     * Handles cancelling an overtime declaration.
     */
    const handleCancelRequest = async () => {
        if (!selectedRequest) return;

        try {
            await deleteOvertimeRequest(selectedRequest);
            notify('Overtime declaration cancelled successfully', { type: 'success' });
            fetchRequests();
        } catch (error: unknown) {
            const errorObj = error as { body?: { message?: string } };
            const errorMessage = errorObj?.body?.message || 'Failed to cancel declaration';
            notify(errorMessage, { type: 'error' });
        } finally {
            setCancelDialogOpen(false);
            setSelectedRequest(null);
        }
    };

    /**
     * Returns the MUI Chip colour for a given status.
     *
     * @param status - The declaration status string.
     * @returns The MUI colour name.
     */
    const getStatusColor = (status: string): 'success' | 'default' => {
        switch (status) {
            case 'Approved': return 'success';
            case 'Cancelled': return 'default';
            default: return 'default';
        }
    };

    /**
     * Formats a duration into a human-readable string.
     *
     * @param hours   - Number of hours.
     * @param minutes - Number of minutes.
     * @returns Formatted duration, e.g. "2h 30m".
     */
    const formatDuration = (hours: number, minutes: number): string => {
        if (minutes === 0) return `${hours}h`;
        if (hours === 0) return `${minutes}m`;
        return `${hours}h ${minutes}m`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">
                        My Overtime Declarations
                    </Typography>
                    <Box display="flex" gap={1}>
                        {canViewAll && (
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/overtime/all-declarations')}
                            >
                                All Declarations
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={() => navigate('/overtime/create')}
                        >
                            Declare Overtime
                        </Button>
                    </Box>
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Start Time</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Task</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No overtime declarations found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((request) => (
                                    <React.Fragment key={request.id}>
                                        <TableRow
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setExpandedRow(expandedRow === request.id ? null : request.id)}
                                        >
                                            <TableCell>
                                                {new Date(request.date + 'T00:00:00Z').toLocaleDateString(undefined, { timeZone: 'UTC' })}
                                            </TableCell>
                                            <TableCell>
                                                {request.start_time}
                                            </TableCell>
                                            <TableCell>
                                                {formatDuration(request.duration_hours, request.duration_minutes)}
                                            </TableCell>
                                            <TableCell>
                                                {request.task_description.length > 60
                                                    ? request.task_description.substring(0, 60) + '...'
                                                    : request.task_description}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={request.status}
                                                    color={getStatusColor(request.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {request.status === 'Approved' && isCurrentMonth(request.date) && (
                                                    <Box display="flex" gap={1}>
                                                        <Button
                                                            size="small"
                                                            color="primary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/overtime/${request.id}/edit`);
                                                            }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedRequest(request.id);
                                                                setCancelDialogOpen(true);
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {/* Inline history row */}
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                                                <Collapse in={expandedRow === request.id} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 2, backgroundColor: '#f9fafb' }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            History
                                                        </Typography>
                                                        {request.history && request.history.length > 0 ? (
                                                            request.history.map((h, idx) => (
                                                                <Typography key={idx} variant="body2" color="textSecondary">
                                                                    {h.action} — {h.created_date
                                                                        ? new Date(h.created_date).toLocaleString()
                                                                        : 'N/A'}
                                                                </Typography>
                                                            ))
                                                        ) : (
                                                            <Typography variant="body2" color="textSecondary">
                                                                No history available
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>

            <Dialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
            >
                <DialogTitle>Cancel Overtime Declaration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel this overtime declaration?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)}>
                        No, Keep It
                    </Button>
                    <Button onClick={handleCancelRequest} color="error" autoFocus>
                        Yes, Cancel Declaration
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};
